import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useAuth } from "../../../../context/AuthContext";
import StoreBrandsTable from "./StoreBrandsTable";
import StoreBrandFormModal from "./StoreBrandFormModal";
import StoreBrandDeleteModal from "./StoreBrandDeleteModal";

export default function StoreBrandsPanel() {
  const { authFetch, user } = useAuth();

  const [brands, setBrands] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [banner, setBanner] = useState({
    open: false,
    type: "",
    message: "",
  });

  const canManage = user?.role === "admin";

  const showBanner = (type, message) => {
    setBanner({
      open: true,
      type,
      message,
    });
  };

  const loadData = async () => {
    setLoading(true);

    try {
      const response = await authFetch("/api/store/brands/");
      const data = await response.json();

      if (!response.ok) {
        showBanner("error", "No se pudieron cargar las marcas");
        return;
      }

      setBrands(data);
    } catch {
      showBanner("error", "Error cargando marcas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const term = query.toLowerCase().trim();

    if (!term) return brands;

    return brands.filter(
      (b) =>
        b.name.toLowerCase().includes(term) ||
        b.code.toLowerCase().includes(term)
    );
  }, [brands, query]);

  const handleCreate = () => {
    setSelectedBrand(null);
    setFormOpen(true);
  };

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setFormOpen(true);
  };

  const handleDeleteOpen = (brand) => {
    setSelectedBrand(brand);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedBrand(null);
  };

  const handleDelete = async () => {
    if (!selectedBrand) return;

    setActionLoadingId(selectedBrand.uuid);

    try {
      const response = await authFetch(
        `/api/store/brands/${selectedBrand.uuid}/delete/`,
        { method: "DELETE" }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        showBanner("error", data?.error || "No se pudo eliminar la marca");
        return;
      }

      setBrands((prev) => prev.filter((b) => b.uuid !== selectedBrand.uuid));
      showBanner("success", data?.message || "Marca eliminada correctamente");
      handleDeleteClose();
    } catch {
      showBanner("error", "Ocurrió un error al eliminar la marca");
    } finally {
      setActionLoadingId("");
    }
  };

  return (
    <Box width="100%">
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={1.4}
        mb={1.8}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Buscar marca"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon
                  sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }}
                />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#fff",
              },
            }}
          />

          <Button
            onClick={loadData}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
          >
            Recargar
          </Button>
        </Stack>

        {canManage && (
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<AddRoundedIcon />}
          >
            Nueva marca
          </Button>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={26} />
      ) : (
        <StoreBrandsTable
          brands={filtered}
          onEdit={handleEdit}
          onDelete={handleDeleteOpen}
          canManage={canManage}
          actionLoadingId={actionLoadingId}
        />
      )}

      <StoreBrandDeleteModal
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        brand={selectedBrand}
        loading={false}
      />

      <StoreBrandFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        brand={selectedBrand}
        reload={loadData}
        showBanner={showBanner}
      />

      <Snackbar
        open={banner.open}
        autoHideDuration={3500}
        onClose={() =>
          setBanner((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={banner.type}
          variant="filled"
          sx={{
            borderRadius: "12px",
            fontWeight: 600,
            fontSize: 13,
            bgcolor:
              banner.type === "success"
                ? "rgba(34,197,94,0.12)"
                : "rgba(220,38,38,0.12)",
            color:
              banner.type === "success"
                ? "#166534"
                : "#dc2626",
            boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
          }}
        >
          {banner.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}